// Shared plumbing for the Settings pages: the busy/saved/error action hook,
// the page head, save buttons, checkbox rows, a shown-once modal, and the
// small parsing helpers the forms lean on.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import './settings.css';

export function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

/** Wraps any async mutation with busy state, a subtle "Saved" flash and a
 * polite error string. One instance per form/section. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await fn();
      setSaved(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, saved, error, run, setError } as const;
}

export function Head(props: { title: string; sub?: string }) {
  return (
    <div className="page-head">
      <div>
        <div className="overline">Store · Settings{props.sub ? ` · ${props.sub}` : ''}</div>
        <h1>{props.title}</h1>
      </div>
    </div>
  );
}

export function SaveBtn(props: {
  busy: boolean;
  saved: boolean;
  onClick: () => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <span className="save-row">
      <button className="btn btn-primary" disabled={props.busy || props.disabled} onClick={props.onClick}>
        {props.busy ? 'Saving…' : (props.label ?? 'Save')}
      </button>
      {props.saved && <span className="saved-flash">Saved</span>}
    </span>
  );
}

export function Check(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="checkline">
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      {props.label}
    </label>
  );
}

export function Modal(props: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="modal-back" onClick={props.onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{props.title}</h3>
        {props.children}
        <div style={{ marginTop: 18, textAlign: 'right' }}>
          <button className="btn btn-outline" onClick={props.onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/** '' → null, otherwise the integer value. */
export const intOrNull = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

export const int = (v: string): number => intOrNull(v) ?? 0;

/** Dollars-string ↔ cents helpers for money inputs. */
export const centsToDollars = (c: number | null): string => (c === null ? '' : (c / 100).toString());
export const dollarsToCents = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
